/**
 * FindingsSectionHeader -- Title with AI Generate button
 */

import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Sparkles } from "lucide-react";
import { Button, ConfirmDialog } from "@/components/ui";
import { useGenerateFindings } from "@/packages/features-shared";
import type { Id } from "@/packages/types";

interface FindingsSectionHeaderProps {
  checklistItemId: Id<"checklistItems">;
  hasAnswers: boolean;
  hasDrafts: boolean;
  isGenerating: boolean;
}

export function FindingsSectionHeader({
  checklistItemId,
  hasAnswers,
  hasDrafts,
  isGenerating,
}: FindingsSectionHeaderProps) {
  const { t } = useTranslation("audits");
  const generateMutation = useGenerateFindings();
  const [showReplaceConfirm, setShowReplaceConfirm] = useState(false);

  function handleGenerate() {
    if (hasDrafts) {
      setShowReplaceConfirm(true);
      return;
    }
    generateMutation.mutateAsync({ checklistItemId });
  }

  function handleConfirmGenerate() {
    generateMutation.mutateAsync({ checklistItemId });
    setShowReplaceConfirm(false);
  }

  return (
    <>
      <div className="flex items-center justify-between">
        <h4 className="text-xs font-medium text-muted uppercase tracking-wider">
          {t("findings.section.title")}
        </h4>
        <Button
          variant="ghost"
          size="sm"
          icon={Sparkles}
          onClick={handleGenerate}
          disabled={!hasAnswers || isGenerating}
          loading={isGenerating}
        >
          {t("findings.section.generate")}
        </Button>
      </div>

      <ConfirmDialog
        open={showReplaceConfirm}
        onClose={() => setShowReplaceConfirm(false)}
      >
        <ConfirmDialog.Title>
          {t("findings.section.replaceTitle")}
        </ConfirmDialog.Title>
        <ConfirmDialog.Body>
          <p className="text-[13px] text-secondary">
            {t("findings.section.replaceBody")}
          </p>
        </ConfirmDialog.Body>
        <ConfirmDialog.Actions
          onConfirm={handleConfirmGenerate}
          onCancel={() => setShowReplaceConfirm(false)}
          variant="danger"
          loading={generateMutation.isPending}
        />
      </ConfirmDialog>
    </>
  );
}
